import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import type { ReportHistoryReportData } from '../api/reportHistory';
import { PipeDataReportDetailsTable } from './PipeDataReportDetailsTable';
import PipeDataChart, { type PipeDataGraphItem } from './PipeDataChart';
import PipeDataOorChart, {
  hasGraphOorIdData,
  hasGraphOorOdData,
} from './PipeDataOorChart';

type PipeDataViewMode = 'table' | 'graph';

interface PipeDataReportVisualizationProps {
  reportData: ReportHistoryReportData | null;
  /** Fallback when reportData.graphPipes is empty (e.g. Redux pipe data). */
  pipeDataGraph?: PipeDataGraphItem[];
}

function hasGraphIdData(pipes: PipeDataGraphItem[], hasInnerRadiusData?: boolean): boolean {
  if (hasInnerRadiusData === false) return false;
  return pipes.some((p) => p.minID != null || p.avgID != null || p.maxID != null);
}

function hasGraphOdData(pipes: PipeDataGraphItem[], hasOuterRadiusData?: boolean): boolean {
  const hasOdValues = pipes.some((p) => p.minOD != null || p.avgOD != null || p.maxOD != null);
  if (!hasOuterRadiusData && !hasOdValues) return false;
  return hasOdValues;
}

function parseOorTolerance(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export const PipeDataReportVisualization: React.FC<PipeDataReportVisualizationProps> = ({
  reportData,
  pipeDataGraph = [],
}) => {
  const [viewMode, setViewMode] = useState<PipeDataViewMode>('table');

  const pipes = reportData?.pipesList ?? [];
  const graphPipes = useMemo(() => {
    const base = (
      reportData?.graphPipes?.length ? reportData.graphPipes : pipeDataGraph
    ) as PipeDataGraphItem[];
    const list = reportData?.pipesList ?? [];
    if (!list.length) return base;

    const listByEndId = new Map(list.map((row) => [String(row.pipeEndID), row]));
    return base.map((graphRow) => {
      const listRow = listByEndId.get(String(graphRow.pipeEndID ?? ''));
      if (!listRow) return graphRow;
      return {
        ...graphRow,
        ooR: graphRow.ooR ?? listRow.ooR,
        ooROD: graphRow.ooROD ?? listRow.ooROD,
      };
    });
  }, [reportData?.graphPipes, reportData?.pipesList, pipeDataGraph]);

  const showIdGraph = hasGraphIdData(graphPipes, reportData?.hasInnerRadiusData);
  const showOdGraph = hasGraphOdData(graphPipes, reportData?.hasOuterRadiusData);
  const showIdOorGraph = hasGraphOorIdData(graphPipes, reportData?.hasInnerRadiusData);
  const showOdOorGraph = hasGraphOorOdData(graphPipes, reportData?.hasOuterRadiusData);
  const hasAnyGraph = showIdGraph || showOdGraph || showIdOorGraph || showOdOorGraph;

  const oorIdLimit = parseOorTolerance(reportData?.tolerances?.oor_id_tolerance);
  const oorOdLimit = parseOorTolerance(reportData?.tolerances?.oor_od_tolerance);

  const reportContentKey = useMemo(() => {
    const pipeKeys = (reportData?.pipesList ?? []).map((p) => p.pipeEndID).join(',');
    if (pipeKeys) return pipeKeys;
    return `${reportData?.generatedAt ?? ''}:${reportData?.version ?? ''}`;
  }, [reportData]);

  useEffect(() => {
    setViewMode('table');
  }, [reportContentKey]);

  if (!reportData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2 text-heritage-grey">
        <p className="text-[13px]">No visualization data available for this report.</p>
        <p className="text-[11px] text-heritage-grey">
          Try selecting a report that includes saved pipe data.
        </p>
      </div>
    );
  }

  const viewToggle = (
    <div
      className="flex items-center gap-2"
      role="group"
      aria-label="Pipe data view mode"
    >
      <span
        className={`text-[13px] font-medium ${viewMode === 'table' ? 'text-text-grey' : 'text-heritage-grey'}`}
      >
        Table
      </span>
      <Switch
        checked={viewMode === 'graph'}
        onCheckedChange={(checked: boolean) => setViewMode(checked ? 'graph' : 'table')}
        aria-label="Toggle between table and graph view"
      />
      <span
        className={`text-[13px] font-medium ${viewMode === 'graph' ? 'text-text-grey' : 'text-heritage-grey'}`}
      >
        Graph
      </span>
    </div>
  );

  const graphPanels = (
    <div className="space-y-4">
      {showIdGraph && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h5 className="text-[14px] font-semibold text-blueprint-blue">Inner Diameter (ID)</h5>
            <PipeDataChart pipeDataGraph={graphPipes} showIDData minimal showOor={false} />
          </CardContent>
        </Card>
      )}
      {showIdOorGraph && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h5 className="text-[14px] font-semibold text-blueprint-blue">Out of Roundness (ID)</h5>
            <PipeDataOorChart
              pipeDataGraph={graphPipes}
              variant="id"
              configLimit={oorIdLimit}
              minimal
            />
          </CardContent>
        </Card>
      )}
      {showOdGraph && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h5 className="text-[14px] font-semibold text-blueprint-blue">Outer Diameter (OD)</h5>
            <PipeDataChart pipeDataGraph={graphPipes} showIDData={false} minimal showOor={false} />
          </CardContent>
        </Card>
      )}
      {showOdOorGraph && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h5 className="text-[14px] font-semibold text-blueprint-blue">Out of Roundness (OD)</h5>
            <PipeDataOorChart
              pipeDataGraph={graphPipes}
              variant="od"
              configLimit={oorOdLimit}
              minimal
            />
          </CardContent>
        </Card>
      )}
      {!hasAnyGraph && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-2">
            <p className="text-[13px] text-heritage-grey">No graph data available for this report.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-[14px] font-semibold text-blueprint-blue">Individual Pipe Details</h4>
        {viewToggle}
      </div>

      {viewMode === 'table' ? (
        <PipeDataReportDetailsTable
          pipes={pipes}
          hasInnerRadiusData={reportData.hasInnerRadiusData}
          hasOuterRadiusData={reportData.hasOuterRadiusData}
        />
      ) : (
        graphPanels
      )}
    </div>
  );
};
